import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Send, Eye, ListPlus, Download, Trash2, Search, X, Loader2, AlertTriangle, RefreshCw,
} from "lucide-react";

const CONTACT_FIELDS = [
  { v: "contact.name",  label: "Contact Name" },
  { v: "contact.phone", label: "Phone" },
  { v: "contact.email", label: "Email" },
  { v: "contact.city",  label: "City (custom field)" },
  { v: "contact.tags",  label: "Tags (joined)" },
];

const resolveField = (path, c) => {
  if (!path || path === "static") return "";
  if (path === "contact.name") return c.name || "";
  if (path === "contact.phone") return c.phone || "";
  if (path === "contact.email") return c.email || "";
  if (path === "contact.city") return c.custom_fields?.city || "";
  if (path === "contact.tags") return (c.tags || []).join(", ");
  if (path.startsWith("contact.custom.")) return c.custom_fields?.[path.slice(15)] || "";
  return "";
};

const WA_UNIT_PRICE_INR = 0.40;   // matches backend PRICING["whatsapp"]

/* ─────────────────── Mini WhatsApp preview bubble ─────────────────── */
function WaPreview({ headerText, headerFormat, bodyText, footerText, buttons }) {
  return (
    <div className="w-full max-w-[300px] rounded-xl border shadow-sm overflow-hidden" style={{ background: "#efe7dd" }}>
      <div className="flex items-center gap-2 px-3 py-2" style={{ background: "#075E54", color: "#fff" }}>
        <div className="h-7 w-7 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-semibold">B</div>
        <div className="min-w-0">
          <div className="text-xs font-semibold truncate">Business</div>
          <div className="text-[10px] opacity-80">online</div>
        </div>
      </div>
      <div className="p-3">
        <div className="ml-1 max-w-[92%] rounded-md p-2 text-[12px] whitespace-pre-wrap break-words" style={{ background: "#fff" }}>
          {headerFormat === "TEXT" && headerText && (
            <div className="font-semibold text-[13px] mb-1">{headerText}</div>
          )}
          {headerFormat && headerFormat !== "TEXT" && headerFormat !== "NONE" && (
            <div className="w-full h-16 mb-1 border rounded-sm bg-zinc-100 flex items-center justify-center text-[10px] text-muted-foreground">
              {headerFormat === "IMAGE" && "📷 Image header"}
              {headerFormat === "VIDEO" && "🎬 Video header"}
              {headerFormat === "DOCUMENT" && "📄 Document"}
            </div>
          )}
          <div className="text-[12px] leading-snug">{bodyText || "—"}</div>
          {footerText && <div className="text-[10px] text-muted-foreground mt-1.5">{footerText}</div>}
          <div className="text-[9px] text-muted-foreground text-right mt-1">12:34 PM</div>
        </div>
        {(buttons || []).length > 0 && (
          <div className="ml-1 mt-1 space-y-0.5">
            {(buttons || []).map((b, i) => (
              <div key={i} className="rounded-md text-[12px] py-1.5 text-center bg-white text-sky-500 shadow-sm">
                {b.text || b.url || b.phone_number}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────── Bulk Actions Panel ─────────────────── */
export default function ContactsBulkPanel({ selectedContacts, onCleared, onChanged, lists }) {
  const [tplList, setTplList] = useState([]);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplErr, setTplErr] = useState("");
  const [q, setQ] = useState("");
  const [pickedKey, setPickedKey] = useState("");            // `${name}|${language}`
  const [showPreview, setShowPreview] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [showList, setShowList] = useState(false);
  const [sending, setSending] = useState(false);
  const [variableMap, setVariableMap] = useState({});       // { "1": {source, value} }

  const count = selectedContacts.length;

  const loadTemplates = async () => {
    setTplLoading(true); setTplErr("");
    try {
      const { data } = await api.get("/whatsapp/templates?status=APPROVED");
      const raw = data.ok ? (data.templates || []) : [];
      const approved = raw.filter(t => (t.status || "").toUpperCase() === "APPROVED");
      setTplList(approved);
    } catch (err) {
      setTplErr(err.response?.data?.detail || "Failed to load templates");
    } finally { setTplLoading(false); }
  };
  useEffect(() => { if (count > 0 && tplList.length === 0 && !tplErr) loadTemplates(); }, [count]);

  const filteredTpls = useMemo(() => {
    if (!q) return tplList;
    const s = q.toLowerCase();
    return tplList.filter(t => t.name.toLowerCase().includes(s) || (t.category || "").toLowerCase().includes(s));
  }, [q, tplList]);

  const picked = useMemo(() => {
    if (!pickedKey) return null;
    const [n, l] = pickedKey.split("|");
    return tplList.find(t => t.name === n && t.language === l) || null;
  }, [pickedKey, tplList]);

  // Detect body variables from picked template
  const bodyVars = useMemo(() => {
    if (!picked) return [];
    const body = (picked.components || []).find(c => (c.type || "").toUpperCase() === "BODY");
    const text = body?.text || picked.body_preview || "";
    const matches = text.match(/\{\{(\d+)\}\}/g) || [];
    return Array.from(new Set(matches.map(m => m.replace(/[^\d]/g, ""))));
  }, [picked]);

  // Reset mapping when picked template changes
  useEffect(() => {
    if (!picked) { setVariableMap({}); return; }
    const seed = {};
    bodyVars.forEach((n, i) => {
      seed[n] = variableMap[n] || { source: i === 0 ? "contact.name" : "static", value: "" };
    });
    setVariableMap(seed);

  }, [pickedKey]);

  const resolveVarsForContact = (c) => {
    const params = {};
    for (const n of bodyVars) {
      const m = variableMap[n] || { source: "static", value: "" };
      const v = m.source === "static" ? m.value : resolveField(m.source, c);
      params[n] = v || "";
    }
    return params;
  };

  // Validate — recipient is invalid if opted_out, no phone, or a body-required variable resolves empty
  const validation = useMemo(() => {
    const valid = []; const invalid = [];
    for (const c of selectedContacts) {
      const reasons = [];
      if (!c.phone) reasons.push("no phone");
      if (c.opted_out) reasons.push("opted out");
      if (c.dnd) reasons.push("DND");
      const params = resolveVarsForContact(c);
      const missing = bodyVars.filter(n => !params[n]);
      if (missing.length) reasons.push(`missing {{${missing.join(",")}}}`);
      (reasons.length ? invalid : valid).push({ contact: c, reasons });
    }
    return { valid, invalid };

  }, [selectedContacts, variableMap, bodyVars]);

  const previewSample = useMemo(() => {
    if (!picked) return null;
    const sample = validation.valid[0]?.contact || selectedContacts[0];
    if (!sample) return null;
    const body = (picked.components || []).find(c => (c.type || "").toUpperCase() === "BODY");
    const header = (picked.components || []).find(c => (c.type || "").toUpperCase() === "HEADER");
    const footer = (picked.components || []).find(c => (c.type || "").toUpperCase() === "FOOTER");
    const btnBlock = (picked.components || []).find(c => (c.type || "").toUpperCase() === "BUTTONS");
    const params = resolveVarsForContact(sample);
    const bodyText = (body?.text || picked.body_preview || "").replace(/\{\{(\d+)\}\}/g, (_, n) => params[n] || `{{${n}}}`);
    return {
      headerFormat: header ? (header.format || "TEXT").toUpperCase() : "NONE",
      headerText: header?.text || "",
      bodyText,
      footerText: footer?.text || "",
      buttons: btnBlock?.buttons || [],
      sampleName: sample.name,
    };

  }, [picked, variableMap, selectedContacts, validation]);

  /* ─────────── Actions ─────────── */
  const doSend = async () => {
    if (!picked) return;
    if (!validation.valid.length) { toast.error("No valid contacts to send to"); return; }
    setSending(true);
    let sent = 0, failed = 0;
    for (const { contact } of validation.valid) {
      const params = resolveVarsForContact(contact);
      const parameters = bodyVars.map(n => ({ type: "text", text: params[n] || "" }));
      const components = parameters.length ? [{ type: "body", parameters }] : [];
      try {
        await api.post("/whatsapp/send-message", {
          to: contact.phone,
          template_name: picked.name,
          template_language: picked.language,
          template_components: components,
        });
        sent += 1;
      } catch (err) {
        failed += 1;
      }
    }
    setSending(false); setShowSend(false);
    toast.success(`Sent ${sent} · Failed ${failed}${validation.invalid.length ? ` · Skipped ${validation.invalid.length}` : ""}`);
    onCleared?.();
  };

  const exportSelected = () => {
    const rows = [["name","phone","email","tags","city","dnd","opted_out","created_at"]];
    selectedContacts.forEach(c => rows.push([
      c.name, c.phone, c.email || "",
      (c.tags || []).join(","),
      c.custom_fields?.city || "",
      c.dnd ? "true" : "false",
      c.opted_out ? "true" : "false",
      c.created_at || "",
    ]));
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `contacts_selected_${selectedContacts.length}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
    toast.success(`Exported ${selectedContacts.length} contact(s)`);
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${count} contact(s)? This is permanent.`)) return;
    try {
      await api.post("/contacts/bulk-delete", selectedContacts.map(c => c.id));
      toast.success(`Deleted ${count}`);
      onCleared?.(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const [addListId, setAddListId] = useState("");
  const [newListName, setNewListName] = useState("");
  const addToList = async () => {
    try {
      let targetId = addListId;
      if (!targetId && newListName.trim()) {
        const r = await api.post("/lists", { name: newListName.trim(), description: "" });
        targetId = r.data.id;
      }
      if (!targetId) { toast.error("Pick a list or enter a new list name"); return; }
      const r = await api.post(`/lists/${targetId}/members/attach`,
                               { contact_ids: selectedContacts.map(c => c.id) });
      toast.success(`${r.data.attached} added${r.data.already_in_list ? `, ${r.data.already_in_list} already in list` : ""}`);
      setShowList(false); setAddListId(""); setNewListName("");
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  if (count === 0) return null;

  const estimatedCost = (WA_UNIT_PRICE_INR * validation.valid.length).toFixed(2);

  return (
    <div className="ml-auto flex items-center gap-2 flex-wrap" data-testid="contacts-bulk-panel">
      <Badge variant="outline" className="rounded-sm px-2 py-1 text-xs font-semibold" data-testid="bulk-selected-count">
        Selected: {count} Contact{count === 1 ? "" : "s"}
      </Badge>

      {/* Approved template picker */}
      <div className="flex items-center gap-1">
        {tplLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        <select
          value={pickedKey}
          onChange={e => setPickedKey(e.target.value)}
          disabled={tplLoading || tplList.length === 0}
          className="h-8 px-2 rounded-sm border bg-background text-xs min-w-[200px]"
          data-testid="bulk-template-select"
        >
          <option value="">{tplLoading ? "Loading templates…" : tplErr ? "Failed to load" : "Approved Template ▼"}</option>
          {filteredTpls.map(t => (
            <option key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>
              {t.name} · {t.language} · [{(t.category || "").toUpperCase()}]{t.variable_count ? ` · ${t.variable_count} var` : ""}
            </option>
          ))}
        </select>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-sm" onClick={loadTemplates} disabled={tplLoading} title="Refresh templates" data-testid="bulk-template-refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${tplLoading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <Button size="sm" variant="outline" className="rounded-sm gap-1" disabled={!picked} onClick={() => setShowPreview(true)} data-testid="bulk-preview-btn">
        <Eye className="h-3.5 w-3.5" /> Preview
      </Button>
      <Button size="sm" className="rounded-sm gap-1" disabled={!picked} onClick={() => setShowSend(true)} data-testid="bulk-send-wa-btn">
        <Send className="h-3.5 w-3.5" /> Send WhatsApp
      </Button>
      <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={() => setShowList(true)} data-testid="bulk-add-list-btn">
        <ListPlus className="h-3.5 w-3.5" /> Add to List
      </Button>
      <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={exportSelected} data-testid="bulk-export-btn">
        <Download className="h-3.5 w-3.5" /> Export
      </Button>
      <Button size="sm" variant="destructive" className="rounded-sm gap-1" onClick={bulkDelete} data-testid="bulk-delete-btn">
        <Trash2 className="h-3.5 w-3.5" /> Delete
      </Button>
      <Button size="sm" variant="ghost" className="rounded-sm gap-1 text-muted-foreground" onClick={onCleared} data-testid="bulk-clear-btn">
        <X className="h-3.5 w-3.5" /> Clear
      </Button>

      {/* Preview modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Preview — {picked?.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left: variable mapping */}
            <div className="space-y-2 text-xs">
              {bodyVars.length === 0 && (
                <div className="p-3 rounded-sm border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800">
                  This template has no variables. It can be sent as-is.
                </div>
              )}
              {bodyVars.length > 0 && (
                <>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Variable mapping · body</div>
                  {bodyVars.map(n => {
                    const cur = variableMap[n] || { source: "contact.name", value: "" };
                    return (
                      <div key={n} className="flex items-center gap-1" data-testid={`bulk-var-${n}`}>
                        <span className="font-mono w-10 text-muted-foreground">{`{{${n}}}`}</span>
                        <select
                          value={cur.source}
                          onChange={e => setVariableMap({ ...variableMap, [n]: { ...cur, source: e.target.value } })}
                          className="h-7 px-1.5 rounded-sm border bg-background text-[11px] flex-1"
                          data-testid={`bulk-var-source-${n}`}
                        >
                          {CONTACT_FIELDS.map(f => <option key={f.v} value={f.v}>{f.label}</option>)}
                          <option value="static">Static Value</option>
                        </select>
                        {cur.source === "static" && (
                          <Input
                            value={cur.value}
                            onChange={e => setVariableMap({ ...variableMap, [n]: { ...cur, value: e.target.value } })}
                            placeholder="Enter static value"
                            className="rounded-sm h-7 text-[11px] flex-1"
                            data-testid={`bulk-var-value-${n}`}
                          />
                        )}
                      </div>
                    );
                  })}
                </>
              )}

              <div className="pt-2 border-t space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Recipient validation</div>
                <div className="grid grid-cols-2 gap-1">
                  <div className="p-2 rounded-sm border">
                    <div className="text-[10px] text-muted-foreground">Valid</div>
                    <div className="font-mono text-sm text-emerald-700">{validation.valid.length}</div>
                  </div>
                  <div className="p-2 rounded-sm border">
                    <div className="text-[10px] text-muted-foreground">Invalid (skipped)</div>
                    <div className="font-mono text-sm text-red-700">{validation.invalid.length}</div>
                  </div>
                </div>
                {validation.invalid.length > 0 && (
                  <details className="text-[10px]">
                    <summary className="cursor-pointer text-muted-foreground">Why skipped?</summary>
                    <ul className="mt-1 space-y-0.5">
                      {validation.invalid.slice(0, 20).map(({ contact, reasons }) => (
                        <li key={contact.id} className="font-mono">
                          <span className="text-foreground">{contact.name || contact.phone}</span>: <span className="text-red-600">{reasons.join(", ")}</span>
                        </li>
                      ))}
                      {validation.invalid.length > 20 && <li className="text-muted-foreground italic">…and {validation.invalid.length - 20} more</li>}
                    </ul>
                  </details>
                )}
              </div>
            </div>

            {/* Right: WhatsApp mobile mock */}
            <div className="flex flex-col items-center gap-2">
              {previewSample ? (
                <>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Preview as: <span className="font-mono text-foreground">{previewSample.sampleName}</span></div>
                  <WaPreview
                    headerFormat={previewSample.headerFormat}
                    headerText={previewSample.headerText}
                    bodyText={previewSample.bodyText}
                    footerText={previewSample.footerText}
                    buttons={previewSample.buttons}
                  />
                </>
              ) : (
                <div className="text-xs text-muted-foreground p-4 text-center">No valid contact to preview</div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Close</Button>
            <Button className="rounded-sm gap-1" disabled={!validation.valid.length} onClick={() => { setShowPreview(false); setShowSend(true); }}>
              <Send className="h-3.5 w-3.5" /> Proceed to Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send confirm modal */}
      <Dialog open={showSend} onOpenChange={setShowSend}>
        <DialogContent className="rounded-sm max-w-md" data-testid="bulk-send-confirm">
          <DialogHeader><DialogTitle>Confirm WhatsApp send</DialogTitle></DialogHeader>
          <div className="space-y-2 text-xs">
            <div className="p-2 rounded-sm border"><span className="text-muted-foreground">Template</span> · <span className="font-mono">{picked?.name}</span> ({picked?.language})</div>
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 rounded-sm border"><div className="text-[10px] text-muted-foreground">Selected</div><div className="font-mono">{count}</div></div>
              <div className="p-2 rounded-sm border"><div className="text-[10px] text-muted-foreground">Valid</div><div className="font-mono text-emerald-700">{validation.valid.length}</div></div>
              <div className="p-2 rounded-sm border"><div className="text-[10px] text-muted-foreground">Skipped</div><div className="font-mono text-red-700">{validation.invalid.length}</div></div>
            </div>
            <div className="p-2 rounded-sm border bg-muted/20">
              <div className="text-[10px] text-muted-foreground">Estimated Meta cost</div>
              <div className="font-mono">₹ {estimatedCost} <span className="text-muted-foreground text-[10px]">(₹{WA_UNIT_PRICE_INR}/msg × {validation.valid.length})</span></div>
            </div>
            {validation.invalid.length > 0 && (
              <div className="flex items-start gap-1 text-[11px] text-amber-800 dark:text-amber-300 p-2 rounded-sm border border-amber-300 bg-amber-50 dark:bg-amber-900/20">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>{validation.invalid.length} contact{validation.invalid.length === 1 ? "" : "s"} will be skipped (opted-out / DND / missing phone / unresolved variable).</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSend(false)} disabled={sending}>Cancel</Button>
            <Button onClick={doSend} disabled={sending || !validation.valid.length} className="rounded-sm gap-1" data-testid="bulk-send-confirm-btn">
              {sending ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending…</> : <><Send className="h-3.5 w-3.5" /> Send to {validation.valid.length}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add to list modal */}
      <Dialog open={showList} onOpenChange={setShowList}>
        <DialogContent className="rounded-sm max-w-md" data-testid="bulk-add-list-dialog">
          <DialogHeader><DialogTitle>Add {count} contact(s) to a list</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Existing list</Label>
              <select value={addListId} onChange={e => { setAddListId(e.target.value); setNewListName(""); }}
                      className="h-9 w-full px-3 rounded-sm border bg-background text-sm" data-testid="bulk-list-select">
                <option value="">— Select an existing list —</option>
                {(lists || []).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 right-0 flex items-center"><div className="w-full border-t" /></div>
              <div className="relative flex justify-center text-[10px] uppercase"><span className="bg-background px-2 text-muted-foreground">or</span></div>
            </div>
            <div>
              <Label className="text-xs">Create a new list</Label>
              <Input value={newListName} onChange={e => { setNewListName(e.target.value); setAddListId(""); }}
                     placeholder="New list name" className="rounded-sm" data-testid="bulk-new-list-name" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowList(false)}>Cancel</Button>
            <Button onClick={addToList} className="rounded-sm" data-testid="bulk-add-list-submit">
              Add to list
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
